import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Compass, Sparkles, ArrowUp } from "lucide-react";
import { PostCard } from "@/components/feed/PostCard";
import { RightSidebar } from "@/components/app/RightSidebar";
import { GenesisBanner } from "@/components/genesis/GenesisBanner";
import { AnnouncementBar } from "@/components/app/AnnouncementBar";
import { FoundersLiveBanner } from "@/components/live/FoundersLiveBanner";
import { StoryTray, StoryViewer, StoryComposer } from "@/components/app/Stories";
import { useCreate } from "@/components/app/AppLayout";
import { usePoll } from "@/hooks/usePoll";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

const TABS = [
  { id: "foryou", label: "For You" },
  { id: "following", label: "Following" },
  { id: "latest", label: "Latest" },
];
const PAGE = 20;
const keyOf = (p) => p.id + (p.reposted_by?.user_id || "");

export default function ForYou() {
  const navigate = useNavigate();
  const openCreate = useCreate();
  const [tab, setTab] = useState("foryou");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [empty, setEmpty] = useState(null);
  const [newCount, setNewCount] = useState(0);
  const [tray, setTray] = useState(null);
  const [composer, setComposer] = useState(false);
  const [viewer, setViewer] = useState(null); // {groups, index, mine}
  const topRef = useRef(null);
  const sentinel = useRef(null);
  const seenQueue = useRef(new Set());

  const load = useCallback(async (kind) => {
    setLoading(true); setEmpty(null); setNewCount(0);
    try {
      const r = await api.feed(kind, 0, PAGE);
      setPosts(r.posts || []);
      setMore(kind === "foryou" ? !!r.has_more : (r.posts || []).length >= PAGE);
      setEmpty(r.empty || (r.posts.length === 0 ? "no_posts" : null));
    } catch (e) { setPosts([]); setEmpty("error"); }
    finally { setLoading(false); }
    // stable api/setters; kind is passed as an argument
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(tab); }, [tab, load]);

  const loadMore = useCallback(async () => {
    if (loadingMore || loading || !more) return;
    setLoadingMore(true);
    try {
      const r = await api.feed(tab, posts.length, PAGE);
      const known = new Set(posts.map(keyOf));
      const fresh = (r.posts || []).filter((p) => !known.has(keyOf(p)));
      setPosts((cur) => [...cur, ...fresh]);
      setMore(tab === "foryou" ? !!r.has_more && fresh.length > 0 : fresh.length >= PAGE);
    } catch { setMore(false); }
    finally { setLoadingMore(false); }
  }, [loadingMore, loading, more, tab, posts]);

  useEffect(() => {
    const el = sentinel.current; if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) loadMore(); }, { rootMargin: "600px" });
    obs.observe(el); return () => obs.disconnect();
  }, [loadMore]);

  // Flush "seen" post ids in batches (For You tab only).
  const flushSeen = useCallback(() => {
    if (seenQueue.current.size === 0) return;
    const ids = [...seenQueue.current]; seenQueue.current.clear();
    api.feedSeen(ids).catch(() => {});
  }, []);
  useEffect(() => { const t = setInterval(flushSeen, 4000); return () => { clearInterval(t); flushSeen(); }; }, [flushSeen]);
  const markSeen = useCallback((id) => { if (tab === "foryou") seenQueue.current.add(id); }, [tab]);

  const loadTray = useCallback(async () => { try { setTray(await api.storiesTray()); } catch {} }, []);
  useEffect(() => { loadTray(); window.addEventListener("story-posted", loadTray); return () => window.removeEventListener("story-posted", loadTray); }, [loadTray]);

  // Poll for new posts (subtle indicator, no auto-jump).
  usePoll(async () => {
    if (loading) return;
    const r = await api.feed(tab, 0, PAGE);
    const fresh = r.posts || [];
    if (posts.length && fresh.length) {
      const known = new Set(posts.map(keyOf));
      const n = fresh.filter((p) => !known.has(keyOf(p))).length;
      setNewCount(n);
    }
  }, 12000, [tab, posts, loading]);

  const showNew = () => { load(tab); topRef.current?.scrollIntoView({ behavior: "smooth" }); };
  const onDeleted = (id) => setPosts((p) => p.filter((x) => x.id !== id));

  return (
    <div className="flex">
      <section className="min-w-0 flex-1">
        <div ref={topRef} className="sticky top-0 z-20 flex items-center justify-center gap-1 border-b border-am-border bg-am-bg/85 px-2 py-2.5 backdrop-blur-xl">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`foryou-tab-${t.id}`}
              className={cn("relative rounded-full px-5 py-1.5 text-sm font-semibold transition-colors", tab === t.id ? "text-white" : "text-am-text2 hover:text-white")}>
              {t.label}
              {tab === t.id && <span className="absolute -bottom-2.5 left-1/2 h-[3px] w-8 -translate-x-1/2 rounded-full aura-gradient" />}
            </button>
          ))}
        </div>

        {(tab === "following" || tab === "foryou") && (
          <StoryTray tray={tray} onAdd={() => setComposer(true)} onOpen={(groups, index, mine) => setViewer({ groups, index, mine })} />
        )}

        <div className="mx-auto max-w-[600px] pt-4">
          <div className="px-3 md:px-5"><FoundersLiveBanner /><AnnouncementBar /></div>
          <GenesisBanner />
        </div>

        {newCount > 0 && (
          <div className="sticky top-14 z-20 mx-auto flex max-w-[600px] justify-center px-3">
            <button onClick={showNew} data-testid="new-posts" className="flex items-center gap-1.5 rounded-full aura-gradient px-4 py-1.5 text-xs font-bold text-black shadow-lg"><ArrowUp className="h-3.5 w-3.5" /> {newCount} new post{newCount > 1 ? "s" : ""}</button>
          </div>
        )}

        <div className="mx-auto max-w-[600px] space-y-4 px-3 py-4 md:px-5" data-testid={`feed-${tab}`}>
          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-am-text3" /></div>
          ) : empty === "not_following" ? (
            <Empty icon={Compass} title="Your Following feed is empty" body="You're not following anyone yet. Discover creators to build your feed." cta="Explore creators" onCta={() => navigate("/explore")} />
          ) : empty === "no_posts" ? (
            <Empty icon={Sparkles} title={tab === "latest" ? "No posts yet" : "Nothing here yet"} body="AuraMax is brand new. Be one of the first to post." cta="Create a post" onCta={openCreate} />
          ) : empty === "error" ? (
            <Empty icon={Sparkles} title="Couldn't load the feed" body="Please try again." cta="Retry" onCta={() => load(tab)} />
          ) : (
            <>
              {posts.map((p) => <SeenTracker key={keyOf(p)} id={p.id} onSeen={markSeen}><PostCard post={p} onDeleted={onDeleted} /></SeenTracker>)}
              <div ref={sentinel} className="h-px" />
              {loadingMore && <div className="flex justify-center py-4" data-testid="feed-loading-more"><Loader2 className="h-5 w-5 animate-spin text-am-text3" /></div>}
              {!more && posts.length > 0 && <p className="py-6 text-center text-xs text-am-text3" data-testid="feed-end">You're all caught up.</p>}
            </>
          )}
        </div>
      </section>
      <RightSidebar />

      <StoryComposer open={composer} onClose={() => setComposer(false)} onPosted={loadTray} />
      {viewer && <StoryViewer groups={viewer.groups} startIndex={viewer.index} mine={viewer.mine} onClose={() => setViewer(null)} onViewed={loadTray} />}
    </div>
  );
}

// Marks a post "seen" after ~1s on screen (≥50% visible).
function SeenTracker({ id, onSeen, children }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    let timer = null; let done = false;
    const obs = new IntersectionObserver(([e]) => {
      if (done) return;
      if (e.isIntersecting) timer = setTimeout(() => { done = true; onSeen(id); obs.disconnect(); }, 1000);
      else if (timer) { clearTimeout(timer); timer = null; }
    }, { threshold: 0.5 });
    obs.observe(el);
    return () => { obs.disconnect(); if (timer) clearTimeout(timer); };
  }, [id, onSeen]);
  return <div ref={ref}>{children}</div>;
}

function Empty({ icon: Icon, title, body, cta, onCta }) {
  return (
    <div className="mx-auto max-w-sm py-20 text-center" data-testid="feed-empty">
      <span className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-2xl border border-am-border bg-am-surface2"><Icon className="h-6 w-6 aura-text" /></span>
      <h3 className="font-display text-lg font-bold text-white">{title}</h3>
      <p className="mt-1.5 text-sm text-am-text2">{body}</p>
      {cta && <button onClick={onCta} className="mt-5 rounded-full aura-gradient px-5 py-2.5 text-sm font-bold text-black">{cta}</button>}
    </div>
  );
}
